import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryClient, queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";

export interface MadupIndustryMappingRow {
  industry: string;
  madup_industry: string;
  updated_at: string;
}

interface MadupIndustryMappingsResponse {
  mappings: MadupIndustryMappingRow[];
}

interface MadupIndustryMappingResponse {
  mapping: MadupIndustryMappingRow;
}

export interface CreateMadupIndustryMappingRequest {
  industry: string;
  madup_industry: string;
}

export interface UpdateMadupIndustryMappingRequest {
  madup_industry: string;
}

interface DeleteMadupIndustryMappingsResponse {
  deleted_count: number;
  deleted_industries: string[];
}

async function fetchMappings(): Promise<MadupIndustryMappingsResponse> {
  return api.get<MadupIndustryMappingsResponse>("/madup-industry-mapping");
}

async function createMapping(
  request: CreateMadupIndustryMappingRequest
): Promise<MadupIndustryMappingResponse> {
  return api.post<MadupIndustryMappingResponse>("/madup-industry-mapping", request);
}

async function updateMapping(
  industry: string,
  request: UpdateMadupIndustryMappingRequest
): Promise<MadupIndustryMappingResponse> {
  return api.put<MadupIndustryMappingResponse>(
    `/madup-industry-mapping/${encodeURIComponent(industry)}`,
    request
  );
}

async function deleteMappings(industries: string[]): Promise<DeleteMadupIndustryMappingsResponse> {
  return api.post<DeleteMadupIndustryMappingsResponse>("/madup-industry-mapping/delete", {
    industries,
  });
}

export function useMadupIndustryMappingsQuery() {
  return useQuery({
    queryKey: queryKeys.madupIndustryMapping.list(),
    queryFn: fetchMappings,
  });
}

export function useCreateMadupIndustryMappingMutation() {
  return useMutation({
    mutationFn: createMapping,
    onSuccess: () => {
      toast({ title: "매핑이 추가되었습니다." });
      queryClient.invalidateQueries({ queryKey: queryKeys.madupIndustryMapping.list() });
    },
    onError: (error: Error) => {
      toast({ title: "매핑 추가 실패", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateMadupIndustryMappingMutation() {
  return useMutation({
    mutationFn: ({ industry, request }: { industry: string; request: UpdateMadupIndustryMappingRequest }) =>
      updateMapping(industry, request),
    onSuccess: () => {
      toast({ title: "매핑이 수정되었습니다." });
      queryClient.invalidateQueries({ queryKey: queryKeys.madupIndustryMapping.list() });
    },
    onError: (error: Error) => {
      toast({ title: "매핑 수정 실패", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteMadupIndustryMappingsMutation() {
  return useMutation({
    mutationFn: deleteMappings,
    onSuccess: (data) => {
      toast({ title: `${data.deleted_count}개 매핑이 삭제되었습니다.` });
      queryClient.invalidateQueries({ queryKey: queryKeys.madupIndustryMapping.list() });
    },
    onError: (error: Error) => {
      toast({ title: "매핑 삭제 실패", description: error.message, variant: "destructive" });
    },
  });
}
